import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowRight } from "lucide-react";

const IMG = "https://customer-assets-7cd3h4nn.emergentagent.net/job_parish-united/artifacts/1bvle8sd_football%20photo.png";

const divisions = [
  { name: "6U", detail: "Ages 5-6 · Intro to football fundamentals" },
  { name: "8U", detail: "Ages 7-8 · Tackle · Foundation" },
  { name: "10U", detail: "Ages 9-10 · Tackle · Skill Building" },
  { name: "12U", detail: "Ages 11-12 · Tackle · Competitive Travel" },
];

export default function Football() {
  return (
    <div data-testid="football-page">
      <section className="relative bg-scu-obsidian text-white overflow-hidden">
        <img src={IMG} alt="Utes Football" className="absolute inset-0 h-full w-full object-cover opacity-55" style={{ objectPosition: "center 35%" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-scu-obsidian/60 to-scu-obsidian" />
        <div className="relative mx-auto max-w-[1400px] px-4 lg:px-8 py-24 md:py-32">
          <div className="text-xs uppercase tracking-[0.35em] text-scu-electric">Program</div>
          <h1 className="mt-4 font-bebas text-6xl md:text-8xl tracking-wider">Utes Football</h1>
          <p className="mt-4 max-w-2xl text-white/80 text-lg">
            Competitive youth football built on fundamentals, discipline and player development. Multiple age divisions across St. Charles Parish.
          </p>
          <div className="mt-8">
            <Button asChild className="bg-scu-purple hover:bg-scu-electric text-white font-bold uppercase tracking-widest h-12 px-6">
              <Link to="/registration">Register Now <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
        <div className="stripe-divider" />
      </section>

      <section className="mx-auto max-w-[1400px] px-4 lg:px-8 py-20">
        <div className="text-xs uppercase tracking-[0.3em] text-scu-purple font-bold">Age Divisions</div>
        <h2 className="mt-3 font-bebas text-5xl tracking-wide text-scu-obsidian">Divisions</h2>
        <p className="mt-2 text-scu-smoke">Placeholder division structure — subject to enrollment.</p>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {divisions.map((d) => (
            <div key={d.name} className="border border-scu-smoke/20 bg-white p-6 rounded-md hover:border-scu-purple hover:-translate-y-1 transition-transform duration-200">
              <div className="font-bebas text-4xl text-scu-purple tracking-wide">{d.name}</div>
              <div className="text-sm text-scu-smoke mt-2">{d.detail}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-4 lg:px-8 py-20">
        <div className="text-xs uppercase tracking-[0.3em] text-scu-purple font-bold">Coaching Staff</div>
        <h2 className="mt-3 font-bebas text-5xl tracking-wide text-scu-obsidian">Utes Football Coaches</h2>
        <p className="mt-2 text-scu-smoke max-w-2xl">Head coach, coordinators and position coaches will be introduced here as they are announced.</p>
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="football-coaches-grid">
          {[
            { role: "Head Coach" },
            { role: "Offensive Coordinator" },
            { role: "Defensive Coordinator" },
            { role: "Position Coach" },
          ].map((c) => (
            <div key={c.role} className="border border-scu-smoke/20 bg-white rounded-md p-5 hover:border-scu-purple transition-colors">
              <div className="aspect-[3/4] w-full rounded-md bg-gradient-to-b from-scu-obsidian via-scu-charcoal to-scu-smoke/60 flex items-center justify-center">
                <span className="font-bebas text-4xl text-white/40 tracking-wide">SCU</span>
              </div>
              <div className="mt-4 text-[10px] uppercase tracking-[0.3em] text-scu-purple font-bold">{c.role}</div>
              <div className="mt-1 font-bebas text-xl tracking-wide text-scu-obsidian">To Be Announced</div>
              <div className="text-xs text-scu-smoke mt-1">Bio and photo coming soon.</div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-scu-off border-y border-scu-smoke/15 py-20">
        <div className="mx-auto max-w-[1400px] px-4 lg:px-8 grid md:grid-cols-3 gap-8">
          {[
            { title: "Coaching Staff", body: "Head coaches, position coaches and staff will be announced. Placeholder." },
            { title: "Practice Info", body: "Locations, times and equipment lists published closer to the season. Placeholder." },
            { title: "Game Schedule", body: "Travel-league schedule and standings coming soon. Placeholder." },
          ].map((c) => (
            <Card key={c.title} className="border-scu-smoke/20 rounded-md p-6">
              <div className="font-bebas text-3xl tracking-wide text-scu-obsidian">{c.title}</div>
              <p className="mt-3 text-scu-smoke">{c.body}</p>
            </Card>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-4 lg:px-8 py-20">
        <div className="text-xs uppercase tracking-[0.3em] text-scu-purple font-bold">Player Development</div>
        <h2 className="mt-3 font-bebas text-5xl tracking-wide text-scu-obsidian">Building The Complete Athlete</h2>
        <p className="mt-4 max-w-3xl text-scu-smoke text-lg leading-relaxed">
          Our football program is designed to develop athletes physically, mentally and emotionally. From
          the earliest fundamentals in 6U through competitive tackle in 12U, players learn accountability, teamwork
          and the game itself — preparing them for the next level of sports and life.
        </p>
      </section>
    </div>
  );
}
